import { useCallback, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { venueInquirySchema, type VenueInquiryInput } from "@/lib/plan-wizard-schema";
import { useSubmitVenueInquiry } from "@/hooks/useSubmitEvent";
import { usePartialLeadCapture, usePlanMenuRedirect } from "@/hooks/usePlanLeadFlow";

const EVENT_TYPES: Array<{ value: VenueInquiryInput["event_type_note"]; label: string }> = [
  { value: "wedding_reception", label: "Wedding reception" },
  { value: "corporate", label: "Corporate event" },
  { value: "private_party", label: "Private party" },
  { value: "other", label: "Other" },
];

export function VenueInquiryBranch() {
  const submit = useSubmitVenueInquiry();
  const goToMenu = usePlanMenuRedirect("venue");
  // Synchronous guard against a double submit creating a duplicate event.
  const submittedRef = useRef(false);

  const form = useForm<VenueInquiryInput>({
    resolver: zodResolver(venueInquirySchema),
    defaultValues: {
      event_type_note: "wedding_reception",
      event_date: "",
      guest_count: 0,
      client_name: "",
      client_email: "",
      client_phone: "",
    },
  });

  // Partial capture (#226) on name/email blur; full submit upgrades it.
  const getContact = useCallback(
    () => ({
      client_name: form.getValues("client_name"),
      client_email: form.getValues("client_email"),
      client_phone: form.getValues("client_phone"),
    }),
    [form],
  );
  const partial = usePartialLeadCapture(getContact, {
    event_type: "venue",
    venue_interest: true,
    source_page_slug: "venue",
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const partialEventId = await partial.awaitId();
    submit.mutate(
      { ...values, partial_event_id: partialEventId },
      {
        // Tracking already fired inside the mutation; go to the venue menu (#1221).
        onSuccess: (r) => goToMenu(r, values.client_name),
        onError: (e) => {
          submittedRef.current = false;
          toast.error(e.message || "Submission failed. Please try again.");
        },
      }
    );
  });

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold">Book our venue</h2>
        <p className="mt-2 text-muted-foreground max-w-lg mx-auto">
          Share a few details and we'll confirm availability. In-house catering
          is included for venue bookings.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <Card>
          <CardContent className="pt-6 grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label id="v-event-type-label">Event type *</Label>
              <Controller
                control={form.control}
                name="event_type_note"
                render={({ field }) => (
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="v-event-type-label">
                    {EVENT_TYPES.map((t) => {
                      const selected = field.value === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => field.onChange(t.value)}
                          className={`inline-flex min-h-[44px] items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background hover:border-primary/40"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-date">Desired date *</Label>
              <Input
                id="v-date"
                type="date"
                {...form.register("event_date")}
                aria-invalid={!!form.formState.errors.event_date}
                aria-describedby={form.formState.errors.event_date ? "v-date-error" : undefined}
              />
              {form.formState.errors.event_date && (
                <p id="v-date-error" role="alert" className="text-xs text-destructive">{form.formState.errors.event_date.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-guests">Guest count *</Label>
              <Input
                id="v-guests"
                type="number"
                min={1}
                {...form.register("guest_count")}
                aria-invalid={!!form.formState.errors.guest_count}
                aria-describedby={form.formState.errors.guest_count ? "v-guests-error" : undefined}
              />
              {form.formState.errors.guest_count && (
                <p id="v-guests-error" role="alert" className="text-xs text-destructive">{form.formState.errors.guest_count.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-name">Name *</Label>
              <Input
                id="v-name"
                {...form.register("client_name", { onBlur: partial.maybeSavePartial })}
                aria-invalid={!!form.formState.errors.client_name}
                aria-describedby={form.formState.errors.client_name ? "v-name-error" : undefined}
              />
              {form.formState.errors.client_name && (
                <p id="v-name-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_name.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-email">Email *</Label>
              <Input
                id="v-email"
                type="email"
                {...form.register("client_email", { onBlur: partial.maybeSavePartial })}
                aria-invalid={!!form.formState.errors.client_email}
                aria-describedby={form.formState.errors.client_email ? "v-email-error" : undefined}
              />
              {form.formState.errors.client_email && (
                <p id="v-email-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_email.message}</p>
              )}
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="v-phone">Phone *</Label>
              <Input
                id="v-phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                {...form.register("client_phone")}
                aria-invalid={!!form.formState.errors.client_phone}
                aria-describedby={form.formState.errors.client_phone ? "v-phone-error" : undefined}
              />
              {form.formState.errors.client_phone && (
                <p id="v-phone-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_phone.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col items-center gap-3 mt-6">
          <Button
            type="submit"
            size="lg"
            className="min-w-[220px] min-h-[44px]"
            disabled={submit.isPending}
          >
            {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send inquiry
          </Button>
          <p className="text-xs text-muted-foreground text-center max-w-md">
            By submitting, you agree we may contact you about your event by email or text.
            Unsubscribe or reply STOP anytime.
          </p>
        </div>
      </form>
    </div>
  );
}
